/**
 * Sprint 1069.E2 — Locale Nudge Toast.
 *
 * If the browser locale (e.g. `es-MX`, `de-AT`) doesn't match the app's
 * current language, surface a ONE-TIME toast suggesting the switch.
 *
 *   "Looks like your device speaks Spanish — switch the app to Español?"
 *
 * Drives ES/DE adoption without forcing it. Friendly, dismissible, and
 * dismissals are remembered forever (per browser) via localStorage.
 *
 * Renders nothing — pure side-effect on mount.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";

const DISMISS_KEY = "sp:locale-nudge-dismissed-v1";
const SUPPORTED = new Set(["en", "es", "de"]);

const COPY = {
  // Each row: (en native message, native button label) for the suggested language.
  es: {
    body: "Parece que tu dispositivo habla Español — ¿cambiar la app a Español?",
    cta: "Cambiar a Español",
    dismiss: "No, gracias",
  },
  de: {
    body: "Es sieht so aus, als spräche dein Gerät Deutsch — App auf Deutsch umstellen?",
    cta: "Auf Deutsch wechseln",
    dismiss: "Nein, danke",
  },
  en: {
    body: "Looks like your device prefers English — switch the app to English?",
    cta: "Switch to English",
    dismiss: "No, thanks",
  },
};

function detectBrowserLang() {
  const raw = (navigator.language || navigator.userLanguage || "").toLowerCase();
  const short = raw.split("-")[0];
  return SUPPORTED.has(short) ? short : null;
}

export default function LocaleNudgeToast() {
  const { language, setLanguage } = useLanguage();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    // 1) Already dismissed permanently
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
    } catch { /* private mode — skip */ }
    // 2) Detect browser language
    const browserLang = detectBrowserLang();
    if (!browserLang) return;
    if (browserLang === language) return;
    // 3) Build the toast in the suggested language (so it's instantly readable).
    const copy = COPY[browserLang] || COPY.en;
    // Delay slightly so it doesn't compete with the app's first paint.
    const tid = setTimeout(() => {
      toast(copy.body, {
        duration: 12000,
        position: "bottom-right",
        action: {
          label: copy.cta,
          onClick: () => {
            try { localStorage.setItem(DISMISS_KEY, browserLang); } catch { /* */ }
            setLanguage?.(browserLang);
          },
        },
        cancel: {
          label: copy.dismiss,
          onClick: () => {
            try { localStorage.setItem(DISMISS_KEY, "dismissed"); } catch { /* */ }
          },
        },
        // testid so QA can find it
        className: "locale-nudge-toast",
      });
    }, 1500);
    return () => clearTimeout(tid);
  }, [language, setLanguage]);

  return null;
}
